import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class OutletMappingReadOnly extends ModelObject<OutletMappingReadOnly> {

    /**
    * The sellar link settings which this mapping belongs to.
    */
    public sellarSettingsId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sellarSettingsId = Guid.parseOrEmpty(jsonSerializedObject.sellarSettingsId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sellarSettingsId = this.sellarSettingsId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sellarSettingsId", rule: (v: any) => !v.isEmpty() || "OutletMappingReadOnly.SellarSettingsId cannot be empty"},
    ];
}


export class OutletMapping extends ModelObject<OutletMapping> {

    /**
    * The Sellar unique identifier for a customer.
    */
    public sellarCustomerIdentifier: number = 0;
    /**
    * The Brewman outlet id the Sellar customer is mapped to.
    */
    public brewmanOutletId: Guid = Guid.createEmpty();
    public readOnly: OutletMappingReadOnly = new OutletMappingReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sellarCustomerIdentifier = jsonSerializedObject.sellarCustomerIdentifier;
          this.brewmanOutletId = Guid.parseOrEmpty(jsonSerializedObject.brewmanOutletId);
          this.readOnly = new OutletMappingReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sellarCustomerIdentifier = this.sellarCustomerIdentifier;
        toRet.brewmanOutletId = this.brewmanOutletId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sellarCustomerIdentifier", rule: (v: any) => v == null || v >= -2147483648 || "SellarCustomerIdentifier is not greater than minimum allowed value: -2147483648"},
            {field: "sellarCustomerIdentifier", rule: (v: any) => v == null || v <= 2147483647 || "SellarCustomerIdentifier is above maximum allowed value: 2147483647"},
            {field: "brewmanOutletId", rule: (v: any) => !v.isEmpty() || "OutletMapping.BrewmanOutletId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class SellarAddress extends ModelObject<SellarAddress> {

    public name: string = "";
    public street: string = "";
    public city: string = "";
    public region: string = "";
    public postalCode: string = "";
    public country: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.name = jsonSerializedObject.name;
          this.street = jsonSerializedObject.street;
          this.city = jsonSerializedObject.city;
          this.region = jsonSerializedObject.region;
          this.postalCode = jsonSerializedObject.postalCode;
          this.country = jsonSerializedObject.country;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.name = this.name;
        toRet.street = this.street;
        toRet.city = this.city;
        toRet.region = this.region;
        toRet.postalCode = this.postalCode;
        toRet.country = this.country;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class SellarCustomer extends ModelObject<SellarCustomer> {

    public sellarCustomerIdentifier: number = 0;
    public name: string = "";
    public email: string = "";
    public telephone: string = "";
    public contactName: string = "";
    public vatNumber: string = "";
    public awrsNumber: string = "";
    public invoiceAddress: SellarAddress = new SellarAddress();
    public deliveryAddress: SellarAddress = new SellarAddress();
    public invoiceEmail: string = "";
    public emailInvoices: boolean = false;
    public websiteUrl: string = "";
    public companyNumber: string = "";
    public applicationFormReturned: boolean = false;
    public hasDefaultInvoiceAddress: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sellarCustomerIdentifier = jsonSerializedObject.sellarCustomerIdentifier;
          this.name = jsonSerializedObject.name;
          this.email = jsonSerializedObject.email;
          this.telephone = jsonSerializedObject.telephone;
          this.contactName = jsonSerializedObject.contactName;
          this.vatNumber = jsonSerializedObject.vatNumber;
          this.awrsNumber = jsonSerializedObject.awrsNumber;
          this.invoiceAddress = new SellarAddress(jsonSerializedObject.invoiceAddress);
          this.deliveryAddress = new SellarAddress(jsonSerializedObject.deliveryAddress);
          this.invoiceEmail = jsonSerializedObject.invoiceEmail;
          this.emailInvoices = jsonSerializedObject.emailInvoices;
          this.websiteUrl = jsonSerializedObject.websiteUrl;
          this.companyNumber = jsonSerializedObject.companyNumber;
          this.applicationFormReturned = jsonSerializedObject.applicationFormReturned;
          this.hasDefaultInvoiceAddress = jsonSerializedObject.hasDefaultInvoiceAddress;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sellarCustomerIdentifier = this.sellarCustomerIdentifier;
        toRet.name = this.name;
        toRet.email = this.email;
        toRet.telephone = this.telephone;
        toRet.contactName = this.contactName;
        toRet.vatNumber = this.vatNumber;
        toRet.awrsNumber = this.awrsNumber;
        toRet.invoiceAddress = this.invoiceAddress.toJsonSerializedObject();
        toRet.deliveryAddress = this.deliveryAddress.toJsonSerializedObject();
        toRet.invoiceEmail = this.invoiceEmail;
        toRet.emailInvoices = this.emailInvoices;
        toRet.websiteUrl = this.websiteUrl;
        toRet.companyNumber = this.companyNumber;
        toRet.applicationFormReturned = this.applicationFormReturned;
        toRet.hasDefaultInvoiceAddress = this.hasDefaultInvoiceAddress;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sellarCustomerIdentifier", rule: (v: any) => v == null || v >= -2147483648 || "SellarCustomerIdentifier is not greater than minimum allowed value: -2147483648"},
            {field: "sellarCustomerIdentifier", rule: (v: any) => v == null || v <= 2147483647 || "SellarCustomerIdentifier is above maximum allowed value: 2147483647"},
            {field: "invoiceAddress", rule: (v: any) => !!v || "invoiceAddress is required"},
            {field: "invoiceAddress", rule: (v: any) => v.validate()},
            {field: "deliveryAddress", rule: (v: any) => !!v || "deliveryAddress is required"},
            {field: "deliveryAddress", rule: (v: any) => v.validate()},
    ];
}


export class SellarOrder extends ModelObject<SellarOrder> {

    /**
    * The Sellar unique identifier for this order.
    */
    public sellarOrderIdentifier: number = 0;
    /**
    * The date and time this order was created in Sellar.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * The Sellar customer identifier.
    */
    public sellarCustomerIdentifier: number = 0;
    /**
    * A description of the customer in Sellar to help with mapping to a Brewman outlet.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public customerDescription: string = "";
    /**
    * The total number of items in the order.
    */
    public totalOrderQuantity: number = 0;
    /**
    * The total net value of the order including delivery.
    */
    public totalNetPrice: number = 0;
    private _brewmanOutletId: Guid | null = null;
    /**
    * The Brewman outlet id mapped to this order during the last import operation. No value here would indicate a missing outlet mapping.
    */
    public get brewmanOutletId(): Guid | null { return this._brewmanOutletId }
    public set brewmanOutletId(value: Guid | null) {
      this._brewmanOutletId = value === undefined ? null : value;
    }
    private _brewmanOrderHeaderId: Guid | null = null;
    /**
    * The Brewman order id assigned to this order on a successful import. If empty, check the error message field for details.
    */
    public get brewmanOrderHeaderId(): Guid | null { return this._brewmanOrderHeaderId }
    public set brewmanOrderHeaderId(value: Guid | null) {
      this._brewmanOrderHeaderId = value === undefined ? null : value;
    }
    /**
    * Error information associated with a failed import.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public errorMessage: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sellarOrderIdentifier = jsonSerializedObject.sellarOrderIdentifier;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.sellarCustomerIdentifier = jsonSerializedObject.sellarCustomerIdentifier;
          this.customerDescription = jsonSerializedObject.customerDescription;
          this.totalOrderQuantity = jsonSerializedObject.totalOrderQuantity;
          this.totalNetPrice = jsonSerializedObject.totalNetPrice;
          this.brewmanOutletId = jsonSerializedObject.brewmanOutletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.brewmanOutletId);
          this.brewmanOrderHeaderId = jsonSerializedObject.brewmanOrderHeaderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.brewmanOrderHeaderId);
          this.errorMessage = jsonSerializedObject.errorMessage;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sellarOrderIdentifier = this.sellarOrderIdentifier;
        toRet.createdAt = this.createdAt.toString();
        toRet.sellarCustomerIdentifier = this.sellarCustomerIdentifier;
        toRet.customerDescription = this.customerDescription;
        toRet.totalOrderQuantity = this.totalOrderQuantity;
        toRet.totalNetPrice = this.totalNetPrice;
        toRet.brewmanOutletId = this.brewmanOutletId == null ? null : this.brewmanOutletId.toString();
        toRet.brewmanOrderHeaderId = this.brewmanOrderHeaderId == null ? null : this.brewmanOrderHeaderId.toString();
        toRet.errorMessage = this.errorMessage;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sellarOrderIdentifier", rule: (v: any) => v == null || v >= -2147483648 || "SellarOrderIdentifier is not greater than minimum allowed value: -2147483648"},
            {field: "sellarOrderIdentifier", rule: (v: any) => v == null || v <= 2147483647 || "SellarOrderIdentifier is above maximum allowed value: 2147483647"},
            {field: "sellarCustomerIdentifier", rule: (v: any) => v == null || v >= -2147483648 || "SellarCustomerIdentifier is not greater than minimum allowed value: -2147483648"},
            {field: "sellarCustomerIdentifier", rule: (v: any) => v == null || v <= 2147483647 || "SellarCustomerIdentifier is above maximum allowed value: 2147483647"},
            {field: "brewmanOutletId", rule: (v: any) => v == null || !v.isEmpty() || "SellarOrder.BrewmanOutletId cannot be empty"},
            {field: "brewmanOrderHeaderId", rule: (v: any) => v == null || !v.isEmpty() || "SellarOrder.BrewmanOrderHeaderId cannot be empty"},
    ];
}


export class FilteredSellarOrdersResult extends ModelObject<FilteredSellarOrdersResult> {

    public matchingCount: number = 0;
    public sellarOrders: (SellarOrder)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.sellarOrders = jsonSerializedObject.sellarOrders.map((v: any) => new SellarOrder(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.sellarOrders = this.sellarOrders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "sellarOrders", rule: (v: any) => !!v || "sellarOrders is required"},
            {field: "sellarOrders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as SellarOrder[]).reduce((acc: Array<string>, curr: SellarOrder) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class SellarOrderFilter extends ModelObject<SellarOrderFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _createdFromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) created at date
    */
    public get createdFromDate(): LocalDate | null { return this._createdFromDate }
    public set createdFromDate(value: LocalDate | null) {
      this._createdFromDate = value === undefined ? null : value;
    }
    private _createdToDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) created at date
    */
    public get createdToDate(): LocalDate | null { return this._createdToDate }
    public set createdToDate(value: LocalDate | null) {
      this._createdToDate = value === undefined ? null : value;
    }
    private _sellarOrderIdentifiers: readonly (number)[] | null = null;
    /**
    * Limit to just orders with one of the given sellar-allocated ids.
    */
    public get sellarOrderIdentifiers(): readonly (number)[] | null { return this._sellarOrderIdentifiers }
    public set sellarOrderIdentifiers(value: readonly (number)[] | null) {
      this._sellarOrderIdentifiers = value === undefined ? null : value;
    }
    private _sellarAccountNames: readonly (string)[] | null = null;
    /**
    * Limit to specified sellar accounts names
    */
    public get sellarAccountNames(): readonly (string)[] | null { return this._sellarAccountNames }
    public set sellarAccountNames(value: readonly (string)[] | null) {
      this._sellarAccountNames = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.createdFromDate = jsonSerializedObject.createdFromDate == null ? null : LocalDate.parse(jsonSerializedObject.createdFromDate);
          this.createdToDate = jsonSerializedObject.createdToDate == null ? null : LocalDate.parse(jsonSerializedObject.createdToDate);
          this.sellarOrderIdentifiers = jsonSerializedObject.sellarOrderIdentifiers == null ? null : jsonSerializedObject.sellarOrderIdentifiers.map((v: any) => v);
          this.sellarAccountNames = jsonSerializedObject.sellarAccountNames == null ? null : jsonSerializedObject.sellarAccountNames.map((v: any) => v);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.createdFromDate = this.createdFromDate == null ? null : this.createdFromDate.toString();
        toRet.createdToDate = this.createdToDate == null ? null : this.createdToDate.toString();
        toRet.sellarOrderIdentifiers = this.sellarOrderIdentifiers == null ? null : this.sellarOrderIdentifiers.map(v => v);
        toRet.sellarAccountNames = this.sellarAccountNames == null ? null : this.sellarAccountNames.map(v => v);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class SellarLinkService extends ServiceBase {

  public async getOutletMappings(sellarSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(OutletMapping)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    sellarSettingsId:sellarSettingsId.toString(),
    };
    const extractor = (response:any) => response.outletMappings.map((v: any) => new OutletMapping(v));;
    return this.makeJsonRequest("sellarLink/v1/getOutletMappings", req, false, extractor, opts);
  };

  public async assignOutletMapping(sellarSettingsId: Guid, sellarCustomerIdentifier: number, brewmanOutletId: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    sellarSettingsId:sellarSettingsId.toString(),
    sellarCustomerIdentifier:sellarCustomerIdentifier,
    brewmanOutletId:brewmanOutletId == null ? null : brewmanOutletId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("sellarLink/v1/assignOutletMapping", req, true, extractor, opts);
  };

  public async getSellarCustomers(sellarSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SellarCustomer)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    sellarSettingsId:sellarSettingsId.toString(),
    };
    const extractor = (response:any) => response.sellarCustomers.map((v: any) => new SellarCustomer(v));;
    return this.makeJsonRequest("sellarLink/v1/getSellarCustomers", req, false, extractor, opts);
  };

  public async getSellarCustomer(sellarSettingsId: Guid, sellarCustomerIdentifier: number, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SellarCustomer> {
    const req = {
    tenantId:api.tenantId.toString(),
    sellarSettingsId:sellarSettingsId.toString(),
    sellarCustomerIdentifier:sellarCustomerIdentifier,
    };
    const extractor = (response:any) => new SellarCustomer(response.sellarCustomer);;
    return this.makeJsonRequest("sellarLink/v1/getSellarCustomer", req, false, extractor, opts);
  };

  public async getSellarOrdersByFilter(filter: SellarOrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredSellarOrdersResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredSellarOrdersResult(response.results);;
    return this.makeJsonRequest("sellarLink/v1/getSellarOrdersByFilter", req, false, extractor, opts);
  };

  public async markAllErroredOrderImportsForRetry(sellarSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    sellarSettingsId:sellarSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("sellarLink/v1/markAllErroredOrderImportsForRetry", req, true, extractor, opts);
  };

  public async initiateOrderImport(sellarSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    sellarSettingsId:sellarSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("sellarLink/v1/initiateOrderImport", req, true, extractor, opts);
  };
}

export const sellarLinkService = new SellarLinkService();